import { Box, Button, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StyleIcon from '@mui/icons-material/Style';
import BoltIcon from '@mui/icons-material/Bolt';
import { Link } from 'react-router-dom';
import SiteHeader, { RPG_URL } from './SiteHeader';
import RaffleFooter from '../NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { imageAssetUrl } from '../../../../../utils/publicAssets';
import AuctionHomeSection from '../../../../Auction/AuctionHomeSection';
import './landing.css';

const ART = 'https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com';
const giveawayImage = imageAssetUrl('giveaway_image_new.jpg');

// Character art for the hero collage. Framed as cards so whatever the source
// background is, it reads as an intentional game card.
const HERO_CARDS = [
  { src: `${ART}/Evee.png`, left: '1%', top: '20%', rot: -12, z: 1, delay: '0s', w: { xs: 116, sm: 148, md: 186 } },
  { src: `${ART}/Syprus.png`, left: '29%', top: '2%', rot: 0, z: 3, delay: '0.7s', w: { xs: 138, sm: 176, md: 220 } },
  { src: `${ART}/Tron.png`, left: '60%', top: '22%', rot: 12, z: 2, delay: '1.4s', w: { xs: 116, sm: 148, md: 186 } }
];

type CardCta = { label: string; to: string; external?: boolean };
type DestCardData = {
  accent: string;
  kicker: string;
  title: string;
  blurb: string;
  img?: string;
  icon?: React.ReactNode;
  cta: CardCta;
};

const CARDS: DestCardData[] = [
  {
    accent: '#34d399',
    kicker: 'Monthly Draw',
    title: 'A$10,000 Giveaway',
    blurb:
      'Grab a pack, earn entries, and go in the draw for A$10,000 in INC — drawn live every month.',
    img: giveawayImage,
    cta: { label: 'Enter the giveaway', to: '/giveaway' }
  },
  {
    // the member dashboard's promo-tile orange and bolt, so the two read as one thing
    accent: '#ff9a3d',
    kicker: 'Limited promotion',
    title: 'Promo Pack',
    // static copy, so nothing here may assume a promotion is running this week
    blurb:
      'A limited one-off pack that enters you in the weekly promo draw, with its own prizes up for grabs. No subscription needed.',
    icon: <BoltIcon sx={{ fontSize: 66 }} />,
    cta: { label: 'See the pack', to: '/promo' }
  },
  {
    accent: '#38e8ff',
    kicker: 'Epic Games Store',
    title: 'Shadows of Satoshi',
    blurb:
      'Step into the INCRYPT RPG and play through the world of Satoshi. Out now on the Epic Games Store.',
    img: `${ART}/Tron.png`,
    cta: { label: 'Play on Epic', to: RPG_URL, external: true }
  },
  {
    accent: '#e8c877',
    kicker: 'Limited First Run',
    title: 'Physical Cards',
    blurb:
      'Pre-order the first run of real, printed INCRYPT trading cards — limited Angelic Faction packs, shipped to your door.',
    icon: <StyleIcon sx={{ fontSize: 66 }} />,
    cta: { label: 'Shop the cards', to: '/physical-cards' }
  },
  {
    accent: '#ffcf40',
    kicker: "We're here to help",
    title: 'Contact Us',
    blurb:
      'Questions about packs, the game, or your account? Reach our team by email or over on Discord.',
    icon: <SupportAgentIcon sx={{ fontSize: 66 }} />,
    cta: { label: 'Get in touch', to: '/contact-us' }
  }
];

const ABOUT = [
  {
    accent: '#38e8ff',
    heading: 'Who Are We?',
    body: "Incrypt is an Australian crypto-powered gaming platform built on the fusion of blockchain technology, immersive gameplay, and community-driven rewards. We're creating a universe where players can battle, explore, and earn — shaping the future of gaming through innovation and decentralised ecosystems."
  },
  {
    accent: '#e8c877',
    heading: 'Our Vision',
    body: "Incrypt's vision is to build a revolutionary crypto-powered gaming universe where players are truly rewarded for their time, skill, and dedication. By merging gaming, blockchain, and community, we're creating an immersive ecosystem where every action holds value — and the journey shapes the future."
  }
];

/** A single navigation card: themed media on top, copy + CTA below. */
const DestCard = ({ data }: { data: DestCardData }) => {
  const { accent, kicker, title, blurb, img, icon, cta } = data;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '18px',
        overflow: 'hidden',
        background:
          'linear-gradient(180deg, rgba(13,20,29,0.92), rgba(7,11,17,0.96))',
        border: '1px solid rgba(140,170,190,0.14)',
        boxShadow: '0 18px 44px -30px rgba(0,0,0,0.9)',
        transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: `${accent}66`,
          boxShadow: `0 26px 60px -30px ${accent}55`
        }
      }}
    >
      {/* Media */}
      <Box sx={{ position: 'relative', height: 176, overflow: 'hidden' }}>
        {img ? (
          <Box
            component="img"
            src={img}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              color: accent,
              background: `linear-gradient(160deg, ${accent}1f, rgba(7,11,17,0.25))`
            }}
          >
            {icon}
          </Box>
        )}
        {/* Wash so titles/overlays stay legible + accent glow at the top */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(120% 80% at 50% 0%, ${accent}22, transparent 58%), linear-gradient(180deg, rgba(7,11,17,0) 34%, rgba(7,11,17,0.82) 100%)`
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography
          sx={{
            font: '800 10px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: accent
          }}
        >
          {kicker}
        </Typography>
        <Typography
          className="inc-orbitron"
          sx={{ mt: 1, fontSize: { xs: 21, md: 23 }, fontWeight: 800, color: '#eef6fb', lineHeight: 1.15 }}
        >
          {title}
        </Typography>
        <Typography sx={{ mt: 1.25, fontSize: 14.5, lineHeight: 1.55, color: '#9fb2c0', flex: 1 }}>
          {blurb}
        </Typography>

        <Box sx={{ mt: 2.5 }}>
          <Button
            {...(cta.external
              ? { component: 'a', href: cta.to, target: '_blank', rel: 'noopener noreferrer' }
              : { component: Link, to: cta.to })}
            endIcon={cta.external ? <NorthEastIcon sx={{ fontSize: 16 }} /> : <ArrowForwardIcon sx={{ fontSize: 18 }} />}
            sx={{
              px: 2.4,
              py: 0.95,
              borderRadius: 999,
              fontWeight: 800,
              fontSize: 14.5,
              textTransform: 'none',
              color: '#04121a',
              background: accent,
              boxShadow: `0 12px 26px -12px ${accent}aa`,
              '&:hover': { background: accent, filter: 'brightness(1.08)' }
            }}
          >
            {cta.label}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const LandingPage = () => {
  return (
    <Box sx={{ background: '#070c13', minHeight: '100vh' }}>
      <SiteHeader />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(140,170,190,0.10)' }}
      >
        {/* Ambient glows */}
        <Box
          className="inc-glow"
          sx={{
            position: 'absolute',
            top: '-12%',
            left: '-8%',
            width: 520,
            height: 520,
            pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(56,232,255,0.16), transparent 62%)'
          }}
        />
        <Box
          className="inc-glow"
          sx={{
            position: 'absolute',
            bottom: '-20%',
            right: '-6%',
            width: 560,
            height: 560,
            pointerEvents: 'none',
            animationDelay: '2s',
            background: 'radial-gradient(circle, rgba(52,211,153,0.14), transparent 64%)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 2.5, md: 3 },
            py: { xs: 6, md: 9 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            alignItems: 'center',
            gap: { xs: 5, md: 4 }
          }}
        >
          {/* Copy */}
          <Box>
            <Typography
              sx={{
                font: '800 11px ui-monospace, Menlo, Consolas, monospace',
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: '#38e8ff'
              }}
            >
              Trading Cards · RPG · Monthly Giveaways
            </Typography>

            <Typography
              className="inc-orbitron"
              sx={{
                mt: 2,
                fontSize: { xs: 46, sm: 60, md: 78 },
                fontWeight: 900,
                lineHeight: 0.98,
                letterSpacing: '0.01em',
                // Plain white to match the INCRYPT logo wordmark in the header.
                color: '#ffffff'
              }}
            >
              INCRYPT
            </Typography>

            <Typography
              className="inc-orbitron"
              sx={{
                mt: 1,
                fontSize: { xs: 18, md: 22 },
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#c7d6e0'
              }}
            >
              Collect · Play · Earn · Win
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                maxWidth: 520,
                fontSize: { xs: 15.5, md: 17 },
                lineHeight: 1.6,
                color: '#9fb2c0'
              }}
            >
              A digital collectible universe you can actually play. Collect cards,
              battle in the TCG, adventure through our RPG, and go in the draw for{' '}
              <Box component="span" sx={{ color: '#eef6fb', fontWeight: 700 }}>
                A$10,000 in INC every month.
              </Box>
            </Typography>

            <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                component={Link}
                to="/giveaway"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 3,
                  py: 1.15,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 15.5,
                  textTransform: 'none',
                  color: '#04140c',
                  background: 'linear-gradient(180deg,#4ade80,#22c55e)',
                  boxShadow: '0 16px 34px -14px rgba(34,197,94,0.8)',
                  '&:hover': { background: 'linear-gradient(180deg,#5eea90,#2ad36a)' }
                }}
              >
                Enter the $10K Giveaway
              </Button>
              <Button
                component="a"
                href={RPG_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<NorthEastIcon sx={{ fontSize: 17 }} />}
                sx={{
                  px: 3,
                  py: 1.15,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 15.5,
                  textTransform: 'none',
                  color: '#e8f4fb',
                  background: 'rgba(56,232,255,0.06)',
                  border: '1px solid rgba(56,232,255,0.5)',
                  '&:hover': { background: 'rgba(56,232,255,0.14)', borderColor: '#38e8ff' }
                }}
              >
                Play the RPG
              </Button>
            </Box>
          </Box>

          {/* Card collage */}
          <Box
            sx={{
              position: 'relative',
              height: { xs: 300, sm: 360, md: 420 },
              width: '100%'
            }}
          >
            {HERO_CARDS.map((c, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: c.left,
                  top: c.top,
                  zIndex: c.z,
                  transform: `rotate(${c.rot}deg)`
                }}
              >
                <Box
                  className="inc-float"
                  sx={{ animationDelay: c.delay, willChange: 'transform' }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: c.w,
                      aspectRatio: '700 / 1150',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '1px solid rgba(160,190,210,0.25)',
                      boxShadow: '0 30px 60px -24px rgba(0,0,0,0.85)',
                      background: 'linear-gradient(180deg, #0e1622, #070c13)'
                    }}
                  >
                    <Box
                      component="img"
                      src={c.src}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(56,232,255,0.10) 0%, transparent 30%, rgba(7,12,19,0.55) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Live auction (renders nothing when there is none) ─────────────── */}
      <AuctionHomeSection />

      {/* ── Destination cards ────────────────────────────────────────────── */}
      <Box component="section" sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2.5, md: 3 }, py: { xs: 6, md: 9 } }}>
        <Typography
          sx={{
            font: '800 11px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: '#8ea3b3',
            textAlign: 'center'
          }}
        >
          Where to next
        </Typography>
        <Typography
          className="inc-orbitron"
          sx={{
            mt: 1.25,
            textAlign: 'center',
            fontSize: { xs: 28, md: 36 },
            fontWeight: 800,
            color: '#eef6fb'
          }}
        >
          Jump in
        </Typography>

        {/* Five cards: one column on a phone, two in between, all five in a row
            from lg. With two columns the fifth would sit alone at half width,
            so there it spans the row. minmax(0,1fr), not 1fr: a card's longest
            word must not widen its column into a sideways scroll. */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              sm: 'repeat(2, minmax(0,1fr))',
              md: 'repeat(2, minmax(0,1fr))',
              lg: 'repeat(5, minmax(0,1fr))'
            },
            gap: { xs: 2.5, md: 3 },
            '& > :last-child': { gridColumn: { sm: '1 / -1', lg: 'auto' } }
          }}
        >
          {CARDS.map((data) => (
            <DestCard key={data.title} data={data} />
          ))}
        </Box>
      </Box>

      {/* ── About ────────────────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2.5, md: 3 }, pb: { xs: 7, md: 10 } }}
      >
        <Typography
          sx={{
            font: '800 11px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: '#8ea3b3',
            textAlign: 'center'
          }}
        >
          About Incrypt
        </Typography>
        <Typography
          className="inc-orbitron"
          sx={{
            mt: 1.25,
            textAlign: 'center',
            fontSize: { xs: 28, md: 36 },
            fontWeight: 800,
            color: '#eef6fb'
          }}
        >
          Who we are
        </Typography>

        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2.5, md: 3 }
          }}
        >
          {ABOUT.map((s) => (
            <Box
              key={s.heading}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '18px',
                background:
                  'linear-gradient(180deg, rgba(13,20,29,0.9), rgba(7,11,17,0.95))',
                border: '1px solid rgba(140,170,190,0.14)'
              }}
            >
              <Typography
                className="inc-orbitron"
                sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: '#eef6fb' }}
              >
                {s.heading}
              </Typography>
              <Box
                sx={{
                  mt: 1.5,
                  mb: 2.5,
                  width: 140,
                  height: '3px',
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${s.accent}, ${s.accent} 55%, transparent)`
                }}
              />
              <Typography
                sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.7, color: '#a7b8c6' }}
              >
                {s.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <RaffleFooter />
    </Box>
  );
};

export default LandingPage;
